import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { HcSort, HcTableDataSource, ModalOptions, ModalService, PaginationComponent } from '@healthcatalyst/cashmere';
import { AdminStringEditorComponent } from '../string-editor/admin-string-editor.component';
import { TypeService } from 'src/app/shared/services/type.service';
import { ProductLineService } from 'src/app/shared/services/product-line.service';
import { StatusService } from 'src/app/shared/services/status.service';
import { ServiceService } from 'src/app/shared/services/service.service';

/** Admin services */
@Component({
    selector: 'pd-admin-services',
    templateUrl: './admin-services.component.html',
    styleUrls: ['./admin-services.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AdminServicesComponent implements OnInit, AfterViewInit, OnDestroy {
    private unsubscribe = new Subject<void>();
    dataSource: HcTableDataSource<any> = new HcTableDataSource();
    serviceList: any[] = [];
    loading = true;
    loadError = false;
    adminServiceDisplayedColumns: string[] = [
        'item',
        'name',
        'type',
        'productLine',
        'status',
        'revisionDate'
    ];
    adminServicePageSize: number = 10;
    filterData: string[] = [];
    filterSelections: string[] = [];
    filterType = '';
    typeFilters: string[] = [];
    statusFilters: string[] = [];
    productLineFilters: string[] = [];
    publishStatusFilters: string[] = [];
    typeText = 'View All';
    statusText = 'View All';
    productLineText = 'View All';
    publishStatusText = 'View All';
    typeKey: { [key: string]: string } = {};
    productLineKey: { [key: string]: string } = {};
    statusKey: { [key: string]: string } = {};


    @ViewChild(HcSort)
    sort: HcSort;

    @ViewChild(PaginationComponent)
    paginator: PaginationComponent;

    get adminServiceListLength(): number {
        return this.dataSource.filteredData ? this.dataSource.filteredData.length : 0;
    }

    constructor(
        private serviceService: ServiceService,
        private typeService: TypeService,
        private productLineService: ProductLineService,
        private statusService: StatusService,
        private modalService: ModalService
    ) { }

    ngOnInit(): void {
        this.typeService.catTypeKeyUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list && this.typeService.category === 'service') {
                this.typeKey = list;
                if (this.productLineKey 
                    && this.statusKey) {
                    this.loadServices();
                }
            } else {
                this.typeService.loadCatTypes('service');
            }
        });

        this.productLineService.productLineKeyUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.productLineKey = list;
                if (this.typeKey
                    && this.statusKey
                    && this.typeService.category === 'service') {
                    this.loadServices();
                }
            } else {
                this.productLineService.refresh();
            }
        });

        this.statusService.statusKeyUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.statusKey = list;
                if (this.typeKey 
                    && this.productLineKey
                    && this.typeService.category === 'service') {
                    this.loadServices();
                }
            } else {
                this.statusService.refresh();
            }
        });
    }

    ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
        this.initSorting();
    }

    loadServices(): void {
        this.serviceService.servicesUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.loading = false;
                this.loadError = false;
                this.serviceList = [];

                // Parse the id fields for table content
                list.forEach(element => {
                    let tempItem = {
                        'item': element.item,
                        'name': element.name,
                        'type': '',
                        'typeID':element.typeID,
                        'productLine': '',
                        'productLineID': element.productLineID,
                        'status': '',
                        'statusID': element.statusID,
                        'revisionDate': element.revisionDate,
                        'serviceID': element.serviceID,
                        'published': element.published
                    };

                    tempItem['type'] = this.formatID( element['typeID'], 'typeID');
                    tempItem['productLine'] = this.formatID( element['productLineID'], 'productLineID');
                    tempItem['status'] = this.formatID( element['statusID'], 'statusID');

                    this.serviceList.push( tempItem );
                });

                this.dataSource = new HcTableDataSource(this.serviceList);

                if (this.sort && this.paginator) {
                    this.dataSource.sort = this.sort;
                    this.dataSource.paginator = this.paginator;
                    this.initSorting();
                }
            } else {
                this.loadError = this.serviceService.loadFailed;
                if (!this.loadError) {
                    this.serviceService.refresh();
                } else {
                    this.loading = false;
                }
            }
        })
    }

    applySearchFilter(filter: string): void {
        this.dataSource.filter = filter.trim().toLowerCase();
    }

    storeSelectedTextValue(filterArray: string[]) {
        switch (this.filterType) {
            case 'type':
                if (filterArray.length == 1) this.typeText = filterArray[0];
                else if (filterArray.length > 1) this.typeText = `${filterArray.length} selected`;
                else this.typeText = 'View All';
                break;
            case 'productLine':
                if (filterArray.length == 1) this.productLineText = filterArray[0];
                else if (filterArray.length > 1) this.productLineText = `${filterArray.length} selected`;
                else this.productLineText = 'View All';
                break;
            case 'status':
                if (filterArray.length == 1) this.statusText = filterArray[0];
                else if (filterArray.length > 1) this.statusText = `${filterArray.length} selected`;
                else this.statusText = 'View All';
                break;
            case 'published':
                if (filterArray.length == 1) this.publishStatusText = filterArray[0];
                else if (filterArray.length > 1) this.publishStatusText = `${filterArray.length} selected`;
                else this.publishStatusText = 'View All';
                break;
            default:
                break;
        }
    }

    storeFilterData(type: string) {
        let filterData: string[] = [];
        this.filterType = type;

        //add clean strings for publish status
        if (type === 'published') {
            filterData.push('Published');
            filterData.push('Unpublished');
        } else {
            for (let i = 0; i < this.serviceList.length; i++) {
                if (this.serviceList[i][type]) {
                    if (!filterData.includes(this.serviceList[i][type])) {
                        filterData.push(this.serviceList[i][type]);
                    }
                }
            }
        }
        this.filterData = filterData;
    }

    filterServices() {
        let filteredServices = [];

        for (let i = 0; i < this.serviceList.length; i++) {
            let typeFilterMatch = this.typeFilters.length === 0;
            if (this.typeFilters.includes(this.serviceList[i]['type'])) {
                typeFilterMatch = true;
            }

            let statusFilterMatch = this.statusFilters.length === 0;
            if (this.statusFilters.includes(this.serviceList[i]['status'])) {
                statusFilterMatch = true;
            }

            let productLineFilterMatch = this.productLineFilters.length === 0;
            if (this.productLineFilters.includes(this.serviceList[i]['productLine'])) {
                productLineFilterMatch = true;
            }

            // check string filter values from filter popoover against boolean values from data
            let publishStatusFilterMatch = this.publishStatusFilters.length === 0;
            if (
                (this.publishStatusFilters.includes('Published') && this.serviceList[i]['published'] === true) ||
                (this.publishStatusFilters.includes('Unpublished') && this.serviceList[i]['published'] === false)
            ) {
                publishStatusFilterMatch = true;
            }

            if (typeFilterMatch && statusFilterMatch && productLineFilterMatch && publishStatusFilterMatch) {
                filteredServices.push(this.serviceList[i]);
            }
        }

        //grab filter that was applied from text
        let textFilter = this.dataSource.filter;

        //set data source to the filtered service list
        this.dataSource = new HcTableDataSource(filteredServices);

        //reset paginator and sort
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;

        //add the text filter
        this.dataSource.filter = textFilter;
    }

    storeFilterArray(filterArray: string[]) {
        this.storeSelectedTextValue(filterArray);

        if (this.filterType === 'type') {
            this.typeFilters = filterArray;
        } else if (this.filterType === 'status') {
            this.statusFilters = filterArray;
        } else if (this.filterType === 'productLine') {
            this.productLineFilters = filterArray;
        } else if (this.filterType === 'published') {
            this.publishStatusFilters = filterArray;
        }

        this.filterServices();
    }

    //create an object from a string
    createObj(obj: any) {
        return JSON.parse(obj);
    }

    openStringEditor(type: string) {
        const options: ModalOptions = {
            closeIcon: true,
            size: 'md',
            data: {
                type: type,
                category: 'Service'
            }
        };

        this.modalService.open(AdminStringEditorComponent, options);
    }

    formatID(id: number, type: string) {
        let reformattedID: string = '';

        if (type === "typeID") {
            reformattedID = this.typeKey[id.toString()];
        } else if (type === "productLineID") {
            reformattedID = this.productLineKey[id.toString()];
        } else if (type === 'statusID') {
            reformattedID = this.statusKey[id.toString()];
        }

        return reformattedID
    }

    initSorting(): void {
        this.dataSource.sortingDataAccessor = (item, property) => {
            switch (property) {     
                case 'revisionDate': {
                    const sortDate = new Date(item.revisionDate);
                    return sortDate.getTime();
                }
                case 'type':
                case 'productLineID':
                case 'status':
                case 'item':
                case 'name': {
                    return item[property];
                }
                default: {
                    return '';
                }
            }
        };
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
